import { create } from 'zustand'
import { authAPI } from '../api/endpoints/auth'
import { RegisterRequest, ResetPasswordRequest, User } from '../types'
import { queryClient } from '../lib/queryClient'

/**
 * Authentication store
 * Manages user authentication state and actions
 * Implements Requirements 1.1, 1.2, 1.3, 1.5
 */
interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => Promise<void> // Promise qilish kerak
  loadUser: () => Promise<void>
  clearError: () => void
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (data: ResetPasswordRequest) => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('auth_token'),
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await authAPI.login({ email, password })
      localStorage.setItem('auth_token', response.token)
      set({
        user: response.user,
        token: response.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error: any) {
      set({
        error: error.message || 'Login failed',
        isLoading: false,
      })
      throw error
    }
  },

  register: async (data: RegisterRequest) => {
    try {
      set({ isLoading: true, error: null })
      await authAPI.register(data)
      set({ isLoading: false, error: null })
    } catch (error: any) {
      set({
        error: error.message || 'Registration failed',
        isLoading: false,
      })
      throw error
    }
  },

  /**
   * Logout current user
   * Clears token, user data AND React Query cache
   * Requirement 1.5: Logout clears token and redirects
   */
  logout: async () => {
    try {
      // 1. API logout chaqirish (agar kerak bo'lsa)
      await authAPI.logout()

      // 2. Local storage'dan tokenni o'chirish
      localStorage.removeItem('auth_token')

      // 3. **React Query cache'ni tozalash - BU MUHIM!**
      await queryClient.clear() // Barcha cache'ni tozalash

      // 4. Yoki ma'lum query'larni invalidate qilish:
      // await queryClient.invalidateQueries() // Barcha query'larni invalidate qilish

      // 5. Auth holatini yangilash
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        error: null,
      })
    } catch (error) {
      console.error('Logout error:', error)
      // Xatolik bo'lsa ham local holatlarni tozalash
      localStorage.removeItem('auth_token')
      await queryClient.clear()
      set({
        user: null,
        token: null,
        isAuthenticated: false,
      })
    }
  },

  /**
   * Load current user from token
   * Called on app initialization
   */
  loadUser: async () => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      set({ isLoading: false, isAuthenticated: false })
      return
    }

    try {
      set({ isLoading: true })
      const user = await authAPI.getCurrentUser()
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      // Token is invalid or expired - cache'ni ham tozalash
      localStorage.removeItem('auth_token')
      await queryClient.clear() // Invalid token bo'lsa ham cache'ni tozalash
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  },

  forgotPassword: async (email: string) => {
    try {
      set({ isLoading: true, error: null })
      await authAPI.forgotPassword(email)
      set({
        isLoading: false,
        error: null,
      })
    } catch (error: any) {
      set({
        error: error.message || 'Failed to send reset code',
        isLoading: false,
      })
      throw error
    }
  },

  resetPassword: async (data: ResetPasswordRequest) => {
    try {
      set({ isLoading: true, error: null })
      await authAPI.resetPassword(data)
      set({
        isLoading: false,
        error: null,
      })
    } catch (error: any) {
      set({
        error: error.message || 'Failed to reset password',
        isLoading: false,
      })
      throw error
    }
  },

  clearError: () => set({ error: null }),
}))
